'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

interface FAQ {
  question: string;
  questionEn: string;
  answer: string;
  answerEn: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: 'كيف يمكنني الاشتراك في بطاقات الخصم؟',
      questionEn: 'How can I subscribe to discount cards?',
      answer: 'يمكنك الاشتراك بسهولة من خلال صفحة بطاقات الاشتراك على موقعنا. اختر البطاقة المناسبة لك (VIP أو عائلية) وأكمل عملية الدفع الآمنة. ستحصل على بطاقتك فوراً بعد إتمام الدفع.',
      answerEn: 'You can easily subscribe through the subscription cards page on our website. Choose the right card for you (VIP or Family) and complete the secure payment process. You will receive your card immediately after payment.',
    },
    {
      question: 'ما هي مدة صلاحية البطاقة؟',
      questionEn: 'What is the card validity period?',
      answer: 'جميع بطاقات الاشتراك صالحة لمدة سنة كاملة من تاريخ الشراء. يمكنك استخدام البطاقة في أي وقت خلال هذه الفترة للحصول على الخصومات من شركائنا.',
      answerEn: 'All subscription cards are valid for a full year from the purchase date. You can use the card at any time during this period to get discounts from our partners.',
    },
    {
      question: 'هل يمكنني استرداد قيمة الاشتراك؟',
      questionEn: 'Can I get a refund for the subscription?',
      answer: 'نعم، نوفر سياسة استرداد مرنة خلال 14 يوماً من تاريخ الشراء إذا لم تستخدم البطاقة. يرجى التواصل مع فريق الدعم لإتمام عملية الاسترداد.',
      answerEn: 'Yes, we provide a flexible refund policy within 14 days of purchase if you have not used the card. Please contact the support team to complete the refund process.',
    },
    {
      question: 'كم عدد الأشخاص الذين يمكنهم استخدام البطاقة؟',
      questionEn: 'How many people can use the card?',
      answer: 'بطاقة VIP تسمح لـ 6 أشخاص بالاستفادة من الخصومات، وكذلك البطاقة العائلية. يمكنك إضافة أفراد عائلتك أو أصدقائك للاستفادة من المزايا.',
      answerEn: 'The VIP card allows 6 people to benefit from discounts, as does the Family card. You can add your family members or friends to benefit from the advantages.',
    },
    {
      question: 'كيف أعرف الشركاء المشاركين في البرنامج؟',
      questionEn: 'How do I know the partners participating in the program?',
      answer: 'يمكنك الاطلاع على قائمة شاملة بجميع الشركاء المشاركين من خلال صفحة شبكة الشركاء على موقعنا. القائمة تتضمن تفاصيل الخصومات المتاحة لكل شريك.',
      answerEn: 'You can view a comprehensive list of all participating partners through the Partners Network page on our website. The list includes details of available discounts for each partner.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3 mb-6"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-border rounded-lg p-4">
              <div className="h-6 bg-muted rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg p-8 border border-border shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-accent/10 text-accent">
          <Icon name="QuestionMarkCircleIcon" size={24} />
        </div>
        <h2 className="text-2xl font-bold text-foreground font-amiri">
          {t('الأسئلة الشائعة', 'Frequently Asked Questions')}
        </h2>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="border border-border rounded-lg overflow-hidden transition-premium">
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between p-4 text-right hover:bg-muted transition-premium"
            >
              <span className="font-medium text-foreground pr-2">{t(faq.question, faq.questionEn)}</span>
              <Icon
                name="ChevronDownIcon"
                size={20}
                className={`text-primary flex-shrink-0 transition-premium ${openIndex === index ? 'rotate-180' : ''}`}
              />
            </button>

            {openIndex === index && (
              <div className="px-4 pb-4 text-muted-foreground border-t border-border pt-4">
                {t(faq.answer, faq.answerEn)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/10">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{t('لم تجد إجابة لسؤالك؟', "Didn't find an answer to your question?")}</span>{' '}
          {t('لا تتردد في التواصل معنا مباشرة عبر نموذج الاتصال أو الهاتف.', 'Feel free to contact us directly via the contact form or phone.')}
        </p>
      </div>
    </div>
  );
};

export default FAQSection;